import BusinessIcon from '@mui/icons-material/Business'
import GroupAddIcon from '@mui/icons-material/GroupAdd'
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography
} from '@mui/material'
import React from 'react'

interface OfficeChoiceStepProps {
  onSelectJoin: () => void
  onSelectCreate: () => void
}

/**
 * OfficeChoiceStep - Presents users with two options: join an existing office or create a new one
 *
 * This is the first step in the onboarding wizard for users without office associations.
 * Displays two large, clickable cards with clear descriptions and icons.
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4
 */
const OfficeChoiceStep: React.FC<OfficeChoiceStepProps> = ({
  onSelectJoin,
  onSelectCreate
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography
          variant='h4'
          sx={{
            fontWeight: 600,
            mb: 2,
            color: 'text.primary'
          }}
        >
          Welcome! Let's get you set up
        </Typography>
        <Typography
          variant='body1'
          sx={{
            color: 'text.secondary',
            maxWidth: 600,
            mx: 'auto'
          }}
        >
          Choose how you'd like to get started with desk booking
        </Typography>
      </Box>

      {/* Choice Cards */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          width: '100%',
          maxWidth: 900
        }}
      >
        {/* Join an Office Card */}
        <Card
          sx={{
            flex: 1,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 4
            }
          }}
        >
          <CardActionArea
            onClick={onSelectJoin}
            data-testid='join-office-button'
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              justifyContent: 'flex-start',
              p: 3
            }}
          >
            <CardContent
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: 2,
                p: 0
              }}
            >
              {/* Icon */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1
                }}
              >
                <GroupAddIcon
                  sx={{
                    fontSize: 40,
                    color: 'primary.contrastText'
                  }}
                />
              </Box>

              {/* Title */}
              <Typography
                variant='h5'
                sx={{
                  fontWeight: 600,
                  color: 'text.primary'
                }}
              >
                Join an Office
              </Typography>

              {/* Description */}
              <Typography
                variant='body2'
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.6
                }}
              >
                Connect with your team by joining an existing office. You'll
                need an invite from your office administrator to get started.
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>

        {/* Create an Office Card */}
        <Card
          sx={{
            flex: 1,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 4
            }
          }}
        >
          <CardActionArea
            onClick={onSelectCreate}
            data-testid='create-office-button'
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              justifyContent: 'flex-start',
              p: 3
            }}
          >
            <CardContent
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: 2,
                p: 0
              }}
            >
              {/* Icon */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: 'secondary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1
                }}
              >
                <BusinessIcon
                  sx={{
                    fontSize: 40,
                    color: 'primary.contrastText'
                  }}
                />
              </Box>

              {/* Title */}
              <Typography
                variant='h5'
                sx={{
                  fontWeight: 600,
                  color: 'text.primary'
                }}
              >
                Create an Office
              </Typography>

              {/* Description */}
              <Typography
                variant='body2'
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.6
                }}
              >
                Set up a new office workspace for your team. You'll be able to
                create desks, invite team members, and manage bookings.
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      </Box>
    </Box>
  )
}

export default OfficeChoiceStep
